<template>
  <div id="incoming_calls_filters" class="flex flex-col md:flex-row items-start justify-start gap-4 py-4">
    <div class="w-full md:w-1/3">
      <label for="incoming-calls-search" class="sr-only">Search</label>
      <div class="relative w-full">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg aria-hidden="true" class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor"
            viewbox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clip-rule="evenodd" />
          </svg>
        </div>
        <input type="text" id="incoming-calls-search"
          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-primary-500 focus:border-primary-500 block h-10 w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
          placeholder="Search" required=""
          :value="search"
          @input="$emit('update:search', $event.target.value)">
      </div>
    </div>

    <div class="flex items-start justify-start gap-4 w-full md:w-1/3 h-10">
      <multi-select
        :model-value="selectedStatuses"
        :options="callStatusesList"
        placeholder="Select statuses"
        class="multiselect--tags-blue placeholder-gray-300 text-sm bg-gray-50 max-w-96"
        input-container-class="bg-gray-50"
        label="label"
        track-by="name"
        :multiple="true"
        :searchable="false"
        :close-on-select="false"
        :group-select="true"
        @update:modelValue="onCallStatusChange">
        <template #selection="{ values, isOpen }">
          <span
            v-if="values.length"
            v-show="!isOpen">
            <span v-if="values.length === 1" class="multiselect__tag pr-2">
              {{ values[0].label }}
            </span>
            <span v-if="values.length > 1"
              class="flex">
              <span class="multiselect__tag pr-2">
                {{ values[0].label }}
              </span>
              and {{ values.length - 1 }} statuses selected
            </span>
          </span>
        </template>
      </multi-select>
    </div>

    <div class="flex ml-auto items-center justify-end gap-4 w-full md:w-1/4 h-10">
      <VueDatePicker
        v-model="mutableDateRange"
        :year-range="[2000, CURRENT_YEAR]"
        :enable-time-picker="false"
        :preset-dates="presetDates"
        :max-date="new Date()"
        :multi-calendars="{ solo: true }"
        placeholder="Select range"
        range />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { dateWithoutTime, daysAgo } from '../../utils/dates'
import MultiSelect from 'vue-multiselect'

const emit = defineEmits(['update:search', 'update:dates', 'update:callStatuses'])

const props = defineProps({
  search: {
    type: String,
    required: true
  },
  callStatuses: {
    type: Array,
    required: true
  }
})

const CURRENT_YEAR = new Date().getFullYear()

const today = dateWithoutTime(new Date())
const defaultDateRange = [daysAgo(29), today]
const presetDates = ref([
  { label: 'Last year', value: [daysAgo(364), today]},
  { label: 'Last 30 days', value: defaultDateRange },
  { label: 'Last 7 days', value: [daysAgo(6), today]},
  { label: 'Today', value: [today, today]}
])

const mutableDateRange = ref(defaultDateRange)

const dateRangeWithoutTime = computed(() => {
  if (!mutableDateRange.value) return mutableDateRange.value
  const [from, to] = mutableDateRange.value
  return [dateWithoutTime(from), dateWithoutTime(to)]
})

const callStatusesList = ref([
  { name: 'answered', label: 'Answered' },
  { name: 'voicemail', label: 'Voicemail' },
  { name: 'missed', label: 'Missed' },
  { name: 'canceled', label: 'Canceled' },
  { name: 'spam', label: 'Spam' }
])

const selectedStatuses = computed(() => props.callStatuses.map((status) => callStatusesList.value.find((statusOption) => statusOption.name === status)))

const onCallStatusChange = (event) => {
  const statuses = event.map((callStatus) => callStatus.name)
  emit('update:callStatuses', statuses)
}

watch(mutableDateRange, () => {
  emit('update:dates', dateRangeWithoutTime.value)
}, {
  deep: true
})

</script>

<style>
#incoming_calls_filters .multiselect__tags {
  @apply rounded-md h-10;

  min-height: auto;
}

#incoming_calls_filters .dp__input {
  @apply rounded-md h-10;
}
</style>
